import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  addPlace,
  deletePlace,
  fetchPlaceDetails,
  fetchPlaces,
  updatePlace,
} from '../../common/api/placesService';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import { TranslationItem } from '../../common/types/translations.types';
import {
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';

export interface PlaceDetails {
  id?: string;
  isDeleted?: boolean;
  latitude: string;
  longitude: string;
  municipalityId: string;
  parentPlaceId?: string;
  nameTranslations: TranslationItem[];
  descriptionTranslations: TranslationItem[];
  showSearchSuggestion: boolean;
}

export interface PlaceListItem {
  id: string;
  name: string;
  showSearchSuggestion: boolean;
}

export interface PlacesFilter extends PaginationParameters {
  searchString: string;
}

export interface PlacesState {
  placeItems: PlaceListItem[];
  placeDetails: PlaceDetails;
  placeItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  placeDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  placeItemsPagination: PaginationResponse;
}

const initialState: PlacesState = {
  placeItems: [],
  placeDetails: {
    id: '',
    isDeleted: false,
    latitude: '',
    longitude: '',
    municipalityId: '',
    parentPlaceId: '',
    nameTranslations: [],
    descriptionTranslations: [],
    showSearchSuggestion: false,
  },
  placeItemsStatus: LOADING_STATUS.IDLE,
  placeDetailsStatus: LOADING_STATUS.IDLE,
  placeItemsPagination: InitialPaginationResponse,
};

export const getPlacesAsync = createAsyncThunk(
  'places/getPlaces',
  async (placesFilter: PlacesFilter, { rejectWithValue }) => {
    try {
      const response = await fetchPlaces(placesFilter);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getPlaceDetailsAsync = createAsyncThunk(
  'places/getPlaceDetails',
  async (placeId: string, { rejectWithValue }) => {
    try {
      const response = await fetchPlaceDetails(placeId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const savePlaceAsync = createAsyncThunk(
  'places/addPlace',
  async (placeData: PlaceDetails, { rejectWithValue }) => {
    try {
      const response = await addPlace(placeData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updatePlaceAsync = createAsyncThunk(
  'places/updatePlace',
  async (placeData: PlaceDetails, { rejectWithValue }) => {
    try {
      const response = await updatePlace(placeData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deletePlaceAsync = createAsyncThunk(
  'places/deletePlace',
  async (placeId: string, { rejectWithValue }) => {
    try {
      const response = await deletePlace(placeId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const placesSlice = createSlice({
  name: 'places',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getPlacesAsync.pending, state => {
        state.placeItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getPlacesAsync.fulfilled, (state, action) => {
        state.placeItemsStatus = LOADING_STATUS.IDLE;
        state.placeItems = action.payload.data;
        state.placeItemsPagination = PopulatePagination(action.payload);
      })
      .addCase(getPlacesAsync.rejected, state => {
        state.placeItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getPlaceDetailsAsync.pending, state => {
        state.placeDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getPlaceDetailsAsync.fulfilled, (state, action) => {
        state.placeDetailsStatus = LOADING_STATUS.IDLE;
        state.placeDetails = action.payload;
      })
      .addCase(getPlaceDetailsAsync.rejected, state => {
        state.placeDetailsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectPlaceItems = (state: RootState) => state.places.placeItems;
export const selectPlaceItemsPagination = (state: RootState) =>
  state.places.placeItemsPagination;
export const selectPlaceItemsLoadingStatus = (state: RootState) =>
  state.places.placeItemsStatus;
export const selectPlaceDetails = (state: RootState) =>
  state.places.placeDetails;
export const selectPlaceDetailsLoadingStatus = (state: RootState) =>
  state.places.placeDetailsStatus;

export default placesSlice.reducer;
